import { Component, computed, inject, input, linkedSignal, output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { CurrentLangService } from '@core/services/common/current-lang.service';

import { SelectItem, SelectValue } from '@shared/components/select/select-item.model';
import { SelectComponent } from '@shared/components/select/select.component';

import { PATH_FILTER_I18N } from '../../path-filter-labels';
import { DurationUnit, joinDuration, splitDuration } from '../value-slot';

const UNITS: readonly DurationUnit[] = ['seconds', 'minutes', 'hours', 'days'];

let nextId = 0;

/** An ISO duration edited as a number and a unit. An empty number is no value. */
@Component({
  selector: 'app-duration-input',
  imports: [SelectComponent],
  templateUrl: './duration-input.component.html',
  styles: `
    :host {
      display: block;
      min-width: 0;
    }
  `
})
export class DurationInputComponent {
  value = input<string | null>(null);
  ariaLabel = input<string>('');

  valueChange = output<string | null>();

  private readonly translate = inject(TranslateService);
  private readonly currentLang = inject(CurrentLangService).currentLang;
  private readonly idPrefix = `duration-input-${nextId++}`;
  protected readonly unitLabelId = `${this.idPrefix}-unit-label`;
  protected readonly unitSelectId = `${this.idPrefix}-unit`;

  protected readonly unitLabel = computed(() => {
    this.currentLang();
    return this.translate.instant(`${PATH_FILTER_I18N}.value.unit_of`, {
      label: this.ariaLabel()
    }) as string;
  });

  private readonly split = computed(() => {
    const iso = this.value();
    return iso === null ? null : splitDuration(iso);
  });

  /** Kept while the number is empty, so a unit picked first is not lost. */
  protected readonly unit = linkedSignal<DurationUnit | null, DurationUnit>({
    source: () => this.split()?.unit ?? null,
    computation: (unit, previous) => unit ?? previous?.value ?? 'minutes'
  });
  protected readonly amountText = computed(() => {
    const split = this.split();
    return split === null ? '' : String(split.amount);
  });

  protected readonly unitItems = computed<SelectItem[]>(() => {
    this.currentLang();
    return UNITS.map((unit) => ({
      id: unit,
      label: this.translate.instant(`${PATH_FILTER_I18N}.value.units.${unit}`) as string
    }));
  });

  protected onAmount(input: HTMLInputElement): void {
    if (input.value.trim() === '') {
      this.valueChange.emit(null);
      return;
    }
    const amount = Number(input.value);
    if (!Number.isFinite(amount) || amount < 0) {
      input.value = this.amountText();
      return;
    }
    this.valueChange.emit(joinDuration(amount, this.unit()));
  }

  protected onUnit(unit: SelectValue): void {
    if (unit === null || Array.isArray(unit)) return;
    this.unit.set(unit as DurationUnit);
    const split = this.split();
    if (split !== null)
      this.valueChange.emit(joinDuration(split.amount, unit as DurationUnit));
  }
}
